import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { 
  MessageCircle, 
  Send, 
  X, 
  Bot, 
  User, 
  Sparkles, 
  Loader2,
  Minimize2,
  Maximize2
} from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function AIChatbot() {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState([
    {
      from: 'ai',
      text: "👋 Hi! I'm your AI HR Assistant. I can help you with:\n\n• Leave policies & balances\n• Payroll & deductions (EPF, SOCSO, PCB)\n• Claims submission\n• Attendance queries\n• General HR questions\n\nHow can I assist you today?",
      time: new Date().toLocaleTimeString('en-MY', { hour: '2-digit', minute: '2-digit' })
    }
  ]);
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId] = useState(() => `session_${Date.now()}`);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && !isMinimized && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen, isMinimized]);

  const sendMessage = async () => {
    if (!message.trim() || isLoading) return;

    const userMessage = {
      from: 'user',
      text: message.trim(),
      time: new Date().toLocaleTimeString('en-MY', { hour: '2-digit', minute: '2-digit' })
    };

    setMessages(prev => [...prev, userMessage]);
    setMessage('');
    setIsLoading(true);

    try {
      const token = localStorage.getItem('hrms_token');
      const response = await axios.post(
        `${API}/ai/chat`,
        {
          message: userMessage.text,
          session_id: sessionId
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      const aiMessage = {
        from: 'ai',
        text: response.data.response,
        time: new Date().toLocaleTimeString('en-MY', { hour: '2-digit', minute: '2-digit' })
      };

      setMessages(prev => [...prev, aiMessage]);
    } catch (error) {
      const errorMessage = {
        from: 'ai',
        text: "I'm sorry, I encountered an error. Please try again or contact HR directly for assistance.",
        time: new Date().toLocaleTimeString('en-MY', { hour: '2-digit', minute: '2-digit' }),
        isError: true
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const quickQuestions = [
    "What's my leave balance?",
    "Explain EPF deduction",
    "How to submit a claim?",
    "Working hours policy?"
  ];

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 z-50 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white p-4 shadow-2xl shadow-violet-500/30 hover:scale-110 transition-all duration-300 group"
        style={{ borderRadius: '20px' }}
      >
        <div className="relative">
          <Bot className="h-7 w-7" />
          <Sparkles className="h-3 w-3 absolute -top-1 -right-1 text-yellow-300 animate-pulse" />
        </div>
        <span className="absolute -top-2 -left-2 bg-emerald-500 text-white text-xs px-2 py-0.5 font-semibold animate-bounce" style={{ borderRadius: '8px' }}>
          AI
        </span>
      </button>
    );
  }

  return (
    <div 
      className={`fixed bottom-6 right-6 z-50 bg-slate-900 shadow-2xl border border-slate-700/50 overflow-hidden transition-all duration-300 ${
        isMinimized ? 'w-72 h-14' : 'w-96 h-[600px]'
      }`}
      style={{ borderRadius: '24px' }}
    >
      {/* Header */}
      <div className="bg-gradient-to-r from-violet-600 to-purple-600 p-4 flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-white/20 flex items-center justify-center" style={{ borderRadius: '12px' }}>
            <Bot className="h-5 w-5 text-white" />
          </div>
          <div>
            <h3 className="text-white font-semibold flex items-center">
              HR AI Assistant
              <Sparkles className="h-3 w-3 ml-1.5 text-yellow-300" />
            </h3>
            {!isMinimized && (
              <p className="text-violet-200 text-xs">Powered by AI • Always here to help</p>
            )}
          </div>
        </div>
        <div className="flex items-center space-x-1">
          <button 
            onClick={() => setIsMinimized(!isMinimized)} 
            className="text-white/80 hover:text-white hover:bg-white/20 p-1.5 transition-colors"
            style={{ borderRadius: '8px' }}
          >
            {isMinimized ? <Maximize2 className="h-4 w-4" /> : <Minimize2 className="h-4 w-4" />}
          </button>
          <button 
            onClick={() => setIsOpen(false)} 
            className="text-white/80 hover:text-white hover:bg-white/20 p-1.5 transition-colors"
            style={{ borderRadius: '8px' }}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {!isMinimized && (
        <>
          {/* Messages */}
          <div className="h-[400px] overflow-y-auto p-4 space-y-4 bg-slate-950">
            {messages.map((msg, index) => (
              <div key={index} className={`flex ${msg.from === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`flex items-start space-x-2 max-w-[85%] ${msg.from === 'user' ? 'flex-row-reverse space-x-reverse' : ''}`}>
                  <div className={`w-8 h-8 flex-shrink-0 flex items-center justify-center ${
                    msg.from === 'user' 
                      ? 'bg-emerald-600' 
                      : msg.isError ? 'bg-red-600' : 'bg-violet-600'
                  }`} style={{ borderRadius: '10px' }}>
                    {msg.from === 'user' ? (
                      <User className="h-4 w-4 text-white" />
                    ) : (
                      <Bot className="h-4 w-4 text-white" />
                    )}
                  </div>
                  <div className={`px-4 py-3 ${
                    msg.from === 'user' 
                      ? 'bg-emerald-600 text-white' 
                      : msg.isError ? 'bg-red-900/50 text-red-200 border border-red-700' : 'bg-slate-800 text-slate-100'
                  }`} style={{ borderRadius: '16px' }}>
                    <p className="text-sm whitespace-pre-wrap">{msg.text}</p>
                    <p className="text-xs opacity-60 mt-1.5">{msg.time}</p>
                  </div>
                </div>
              </div>
            ))}
            
            {isLoading && (
              <div className="flex justify-start">
                <div className="flex items-start space-x-2">
                  <div className="w-8 h-8 bg-violet-600 flex items-center justify-center" style={{ borderRadius: '10px' }}>
                    <Bot className="h-4 w-4 text-white" />
                  </div>
                  <div className="bg-slate-800 px-4 py-3 flex items-center space-x-2" style={{ borderRadius: '16px' }}>
                    <Loader2 className="h-4 w-4 text-violet-400 animate-spin" />
                    <span className="text-sm text-slate-400">Thinking...</span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Quick Questions */}
          {messages.length <= 2 && (
            <div className="px-4 py-2 bg-slate-900 border-t border-slate-800">
              <p className="text-xs text-slate-500 mb-2">Quick questions:</p>
              <div className="flex flex-wrap gap-2">
                {quickQuestions.map((q, i) => (
                  <button
                    key={i}
                    onClick={() => {
                      setMessage(q);
                      setTimeout(() => sendMessage(), 100);
                    }}
                    className="text-xs px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 transition-colors"
                    style={{ borderRadius: '8px' }}
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Input */}
          <div className="p-4 bg-slate-900 border-t border-slate-800">
            <div className="flex items-center space-x-2">
              <input
                ref={inputRef}
                type="text"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ask me anything about HR..."
                disabled={isLoading}
                className="flex-1 bg-slate-800 border border-slate-700 px-4 py-2.5 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent text-sm disabled:opacity-50"
                style={{ borderRadius: '12px' }}
              />
              <button
                onClick={sendMessage}
                disabled={isLoading || !message.trim()}
                className="bg-violet-600 hover:bg-violet-700 disabled:bg-slate-700 disabled:cursor-not-allowed text-white p-2.5 transition-colors"
                style={{ borderRadius: '12px' }}
              >
                <Send className="h-4 w-4" />
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
